import * as React from 'react'

/**
 * Experimental audience selector for the ontology UI. Persisted in
 * localStorage so the choice survives reloads, and exposed via a
 * sync-external-store hook so toggling from the settings page or
 * insights page re-renders the app live.
 *
 * Storage key intentionally namespaced under `ontology-` (rather than
 * `operator-`) because the ontology-ui app owns the selector — the
 * technician/operator mode is just a mounted sub-tree.
 */
const OPERATOR_VIEW_STORAGE_KEY = 'ontology-operator-view'
const OPERATOR_VIEW_EVENT = 'ontology-operator-view-change'

export type OperatorView = 'technician' | 'manager' | 'engineer' | 'core'

export const OPERATOR_VIEW_OPTIONS: ReadonlyArray<{
  value: OperatorView
  label: string
  description: string
}> = [
  {
    value: 'technician',
    label: 'Technician',
    description: 'Work-order inbox and Assist for floor technicians.'
  },
  {
    value: 'manager',
    label: 'Manager',
    description: 'Summary view for value and completion status.'
  },
  {
    value: 'engineer',
    label: 'Engineer',
    description: 'Full reasoning, confidence, and review controls.'
  },
  {
    value: 'core',
    label: 'Core',
    description: 'Dashboards, configure, and chat — no plant graph or branching.'
  }
]

/** True when the current view is the trimmed Core edition. */
export function isCoreView(view: OperatorView): boolean {
  return view === 'core'
}

export const DEFAULT_OPERATOR_VIEW: OperatorView = 'engineer'

function parseOperatorView(raw: string | null): OperatorView {
  if (raw === 'technician' || raw === 'manager' || raw === 'engineer' || raw === 'core') return raw
  if (raw === 'true') return 'technician'
  if (raw === 'false') return DEFAULT_OPERATOR_VIEW
  return DEFAULT_OPERATOR_VIEW
}

function readOperatorView(): OperatorView {
  if (typeof window === 'undefined' || !('localStorage' in window)) return DEFAULT_OPERATOR_VIEW

  try {
    return parseOperatorView(window.localStorage.getItem(OPERATOR_VIEW_STORAGE_KEY))
  } catch {
    return DEFAULT_OPERATOR_VIEW
  }
}

function subscribeOperatorView(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined

  const handleStorage = (event: StorageEvent) => {
    if (event.key === OPERATOR_VIEW_STORAGE_KEY) listener()
  }

  window.addEventListener(OPERATOR_VIEW_EVENT, listener)
  window.addEventListener('storage', handleStorage)

  return () => {
    window.removeEventListener(OPERATOR_VIEW_EVENT, listener)
    window.removeEventListener('storage', handleStorage)
  }
}

export function getOperatorView(): OperatorView {
  return readOperatorView()
}

export function setOperatorView(view: OperatorView): void {
  if (typeof window === 'undefined' || !('localStorage' in window)) return

  try {
    if (view === DEFAULT_OPERATOR_VIEW) {
      window.localStorage.removeItem(OPERATOR_VIEW_STORAGE_KEY)
    } else {
      window.localStorage.setItem(OPERATOR_VIEW_STORAGE_KEY, view)
    }
    window.dispatchEvent(new Event(OPERATOR_VIEW_EVENT))
  } catch {
    // Ignore storage failures; the selector is a client-side convenience.
  }
}

export function useOperatorView(): OperatorView {
  return React.useSyncExternalStore(
    subscribeOperatorView,
    readOperatorView,
    () => DEFAULT_OPERATOR_VIEW
  )
}
